import React, { useState } from 'react';
import { Copy, Check, List, ChevronRight, ChevronDown } from 'lucide-react';
import type { SSELogEntry } from '../../api';
import { copyText } from '../../utils/clipboard';

/** Color-code per SSE event type so the stream is scannable. */
const EVENT_COLOR: Record<string, string> = {
  thought: 'text-purple-600',
  tool_call: 'text-blue-600',
  tool_result: 'text-amber-600',
  answer: 'text-green-600',
  meta: 'text-slate-600',
  error: 'text-red-600',
  done: 'text-slate-400',
  heartbeat: 'text-slate-300',
};

/**
 * SSE log panel — captures every raw SSE bundle (id+event+data, heartbeat
 * comments included) for debugging and bug reports.
 *
 * The per-entry [📋] button copies that single bundle in raw SSE wire format.
 * The header [📋 전체 복사] copies the full log — paste-ready for
 * `tests/fixtures/legacy-sse-stream.ts` when reproducing issues.
 *
 * Spec: docs/design/01-api-reference.md §5 SSE 프로토콜 규칙 참조.
 */
const SSELogPanel: React.FC<{
  entries: SSELogEntry[];
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ entries, isExpanded, onToggle }) => {
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());
  const [copiedId, setCopiedId] = useState<number | 'all' | null>(null);

  const copyToClipboard = (text: string, id: number | 'all') => {
    // Use shared copyText() — has textarea+execCommand fallback for non-secure
    // HTTP contexts (project's nginx runs HTTP without SSL; `navigator.clipboard`
    // is undefined there). Returns false on failure so the toast doesn't lie.
    if (copyText(text)) {
      setCopiedId(id);
      setTimeout(() => setCopiedId((curr) => (curr === id ? null : curr)), 1000);
    }
  };

  const copyAll = () => {
    const full = entries.map((e) => e.rawLine).join('\n\n');
    copyToClipboard(full, 'all');
  };

  const toggleEntry = (id: number) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className="border-l border-slate-200 bg-slate-50 flex flex-col"
      style={{ width: isExpanded ? '400px' : '48px' }}
      data-testid="sse-log-panel"
    >
      <button
        onClick={onToggle}
        className="p-3 border-b border-slate-200 hover:bg-slate-100 transition-colors flex items-center gap-2"
        title={isExpanded ? 'SSE 로그 접기' : 'SSE 로그 보기'}
      >
        <List size={18} className="text-slate-600" />
        {isExpanded && <span className="text-sm font-medium text-slate-700">SSE 로그</span>}
        {isExpanded && entries.length > 0 && (
          <span className="ml-auto text-xs text-slate-400">{entries.length}</span>
        )}
      </button>

      {isExpanded && (
        <>
          <div className="p-2 border-b border-slate-200 flex items-center justify-between">
            <span className="text-xs text-slate-500">최근 {entries.length}건</span>
            <button
              onClick={copyAll}
              disabled={entries.length === 0}
              data-testid="sse-log-copy-all"
              className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              title="raw SSE 포맷으로 전체 복사"
            >
              {copiedId === 'all' ? <Check size={12} /> : <Copy size={12} />}
              {copiedId === 'all' ? '복사됨' : '전체 복사'}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {entries.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-8">
                전송 후 SSE 이벤트가 여기에 표시됩니다
              </div>
            ) : (
              entries.map((entry) => {
                const color = EVENT_COLOR[entry.event] ?? 'text-slate-600';
                const isExpandedEntry = expandedEntries.has(entry.id);
                return (
                  <div
                    key={`${entry.id}-${entry.timestamp}`}
                    className="border border-slate-200 rounded bg-white"
                    data-testid={`sse-log-entry-${entry.event}`}
                  >
                    <div className="flex items-center gap-1 px-2 py-1">
                      <button
                        onClick={() => toggleEntry(entry.id)}
                        className="flex items-center gap-1 flex-1 text-left min-w-0"
                      >
                        {isExpandedEntry ? (
                          <ChevronDown size={12} className="text-slate-400 shrink-0" />
                        ) : (
                          <ChevronRight size={12} className="text-slate-400 shrink-0" />
                        )}
                        <span className="text-[10px] text-slate-400 font-mono shrink-0">
                          #{entry.id}
                        </span>
                        <span className={`text-xs font-mono font-medium ${color} shrink-0`}>
                          {entry.event}
                        </span>
                        <span className="text-xs text-slate-500 font-mono truncate">
                          {entry.parsed ? JSON.stringify(entry.parsed) : ''}
                        </span>
                      </button>
                      <button
                        onClick={() => copyToClipboard(entry.rawLine, entry.id)}
                        data-testid={`sse-log-copy-${entry.id}`}
                        className="p-1 text-slate-400 hover:text-slate-600"
                        title="이 이벤트만 raw SSE로 복사"
                      >
                        {copiedId === entry.id ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                    {isExpandedEntry && (
                      <pre className="text-[11px] bg-slate-900 text-slate-100 p-2 rounded-b font-mono overflow-x-auto whitespace-pre-wrap">
                        {entry.rawLine}
                      </pre>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SSELogPanel;
